import React, { FC, useEffect, useState } from 'react';
import Thread from './Thread';
import Composer from './Composer';
import { gql, useMutation } from '@apollo/client';
import { createStyles, makeStyles, Theme, Typography } from '@material-ui/core';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';

interface ChatProps {
  threadId?: string | null;
  createThread: () => Promise<string>;
  messageContent: string;
  resetSelectedTab: () => void;
}

const Chat: FC<ChatProps> = ({ threadId: threadIdProp, createThread, messageContent, resetSelectedTab }) => {
  const classes = useStyles();
  const [threadId, setThreadId] = useState(threadIdProp);

  useEffect(() => {
    if (threadIdProp) setThreadId(threadIdProp);
  }, [threadIdProp]);

  const [sendMessage] = useMutation(gql`
    mutation sendMessage($threadId: String!, $content: String!) {
      sendMessage(threadId: $threadId, content: $content)
    }
  `);

  const handleSend = async (content: string) => {
    const resolvedThreadId = threadId || (await createThread());

    await sendMessage({
      variables: {
        threadId: resolvedThreadId,
        content,
      },
    });

    if (!threadId) {
      setThreadId(resolvedThreadId);
    }

    resetSelectedTab();
  };

  return (
    <div className={classes.root}>
      {threadId ? (
        <Thread threadId={threadId} />
      ) : (
        <div className={classes.emptyState}>
          <ChatBubbleOutlineIcon className={classes.emptyIcon} />
          <Typography variant="h6" className={classes.emptyTitle}>
            No messages yet
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Send a message to start the conversation
          </Typography>
        </div>
      )}
      <Composer initialMessage={messageContent} onSend={handleSend} />
    </div>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minWidth: 420,
      minHeight: 0,
    },
    emptyState: {
      flex: '1 1 auto',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 200,
      gap: theme.spacing(1),
      opacity: 0.5,
    },
    emptyIcon: {
      fontSize: '3rem',
      marginBottom: theme.spacing(0.5),
    },
    emptyTitle: {
      fontWeight: 500,
    },
  })
);

export default Chat;
